import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export interface SavedRecipeIngredient {
  id: string;
  quantity_grams: number;
  taco_food_id: number;
  taco_foods: {
    id: number;
    description: string;
    category: string;
    energy_kcal: number | null;
    protein_g: number | null;
    lipid_g: number | null;
    carbohydrate_g: number | null;
    fiber_g: number | null;
  };
}

export interface SavedRecipe {
  id: string;
  name: string;
  user_id: string;
  is_global: boolean;
  visible_basic: boolean;
  visible_plus: boolean;
  visible_complete: boolean;
  icon: string | null;
  banner_image_path: string | null;
  banner_image_url?: string | null;
  created_at: string;
  recipe_ingredients: SavedRecipeIngredient[];
}

export function getRecipeTotals(recipe: SavedRecipe) {
  return recipe.recipe_ingredients.reduce(
    (acc, ing) => {
      const factor = ing.quantity_grams / 100;
      const food = ing.taco_foods;
      return {
        kcal: acc.kcal + (food.energy_kcal || 0) * factor,
        protein: acc.protein + (food.protein_g || 0) * factor,
        carbs: acc.carbs + (food.carbohydrate_g || 0) * factor,
        fat: acc.fat + (food.lipid_g || 0) * factor,
        fiber: acc.fiber + (food.fiber_g || 0) * factor,
        grams: acc.grams + ing.quantity_grams,
      };
    },
    { kcal: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, grams: 0 }
  );
}

export function useSavedRecipes() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<SavedRecipe[]>([]);
  const [globalRecipes, setGlobalRecipes] = useState<SavedRecipe[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRecipes = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await apiFetch("api-recipes", { action: "list" });
      setRecipes(res.data || []);
      setGlobalRecipes(res.globalRecipes || []);
    } catch (e: any) {
      // Plan restriction (403) — user doesn't have access, just leave empty lists
      if (e?.message?.includes("PRO FIT") || e?.message?.includes("403")) {
        setRecipes([]);
        setGlobalRecipes([]);
      } else {
        console.error("Failed to fetch recipes:", e);
      }
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  const deleteRecipe = useCallback(async (id: string) => {
    await apiFetch("api-recipes", { action: "delete", id }, { method: "DELETE" });
    await fetchRecipes();
  }, [fetchRecipes]);

  const updateVisibility = useCallback(async (
    recipeId: string,
    visibility: { visible_basic: boolean; visible_plus: boolean; visible_complete: boolean }
  ) => {
    await apiFetch("api-recipes", { action: "update-visibility" }, {
      method: "PUT",
      body: { recipe_id: recipeId, ...visibility },
    });
    await fetchRecipes();
  }, [fetchRecipes]);

  const updateRecipe = useCallback(async (
    recipeId: string,
    data: {
      name: string;
      ingredients: { taco_food_id: number; quantity_grams: number }[];
      is_global: boolean;
      visible_basic: boolean;
      visible_plus: boolean;
      visible_complete: boolean;
      icon: string | null;
      bannerFile?: File | null;
    }
  ) => {
    if (data.bannerFile) {
      const { compressImage } = await import("@/lib/imageCompressor");
      const compressedBanner = await compressImage(data.bannerFile);
      const formData = new FormData();
      formData.append("recipe_id", recipeId);
      formData.append("name", data.name);
      formData.append("ingredients", JSON.stringify(data.ingredients));
      formData.append("is_global", data.is_global ? "true" : "false");
      formData.append("visible_basic", data.visible_basic ? "true" : "false");
      formData.append("visible_plus", data.visible_plus ? "true" : "false");
      formData.append("visible_complete", data.visible_complete ? "true" : "false");
      if (data.icon) formData.append("icon", data.icon);
      formData.append("banner_image", compressedBanner);

      await apiFetch("api-recipes", { action: "update" }, {
        method: "PUT",
        formData,
      });
    } else {
      await apiFetch("api-recipes", { action: "update" }, {
        method: "PUT",
        body: {
          recipe_id: recipeId,
          name: data.name,
          ingredients: data.ingredients,
          is_global: data.is_global,
          visible_basic: data.visible_basic,
          visible_plus: data.visible_plus,
          visible_complete: data.visible_complete,
          icon: data.icon,
        },
      });
    }
    await fetchRecipes();
  }, [fetchRecipes]);

  return { recipes, globalRecipes, loading, fetchRecipes, deleteRecipe, updateVisibility, updateRecipe };
}
